import React, {FC} from 'react';
import styled from 'styled-components';

import {useToggle} from '../../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ApplicationModel} from '../../../../../../models/api/applicationModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {TopicTypeModel} from '../../../../../../models/api/integrationModel';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {InboxesVisualConversationSelection} from '../../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {IntegrationTopicIcon} from '../../../../../common/integrationTopicIcon';
import {ConversationSummaryContainer} from '../../conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversationSummary/conversationSummarySections';
import {ConversationHeaderPill, StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderPillText} from '../conversationHeaderPillText';
import {TopicDropdownContainer} from './topicDropdownContainer';
import {useConversationsHandlers} from './useConversationHandlers';

/*
 * Props.
 */

interface ConversationHeaderReferencePreviewerPillProps {
  panelId: ViewerPanelId;
  conversation?: ConversationModel;
  teammateId: number;
  topic: TopicModel;
  application?: ApplicationModel;
  topicType?: TopicTypeModel;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Style.
 */

const StyledIntegrationTopicIcon = styled(IntegrationTopicIcon)`
  margin: 0;
  padding: 0;
`;

const StyledTopicIntegrationWrapper = styled.div`
  width: 19px;
  height: 19px;
  display: inline-block;
  margin: 0px -3px -2px -4px;
`;

const StyledApplicationWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  margin-right: 4px;
`;

const StyledApplicationImg = styled.img`
  width: 14px;
  height: 14px;
`;

const StyledConversationHeaderPillText = styled(ConversationHeaderPillText)`
  text-transform: none;
  color: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Component.
 */

export const ConversationHeaderReferencePreviewerPill: FC<ConversationHeaderReferencePreviewerPillProps> = (
  props,
) => {
  const {panelId, conversation, teammateId, topic, conversationSelection, topicType, application} = props;
  const [isConversationSummaryOpen, toggleIsConversationSummaryOpen] = useToggle();

  const isLinkTopic = Boolean(topicType || topic.type === 'web');
  const isApplicationTopic = topic.type.startsWith('app_');
  const conversationsHandlers = useConversationsHandlers(conversationSelection, topic.id);

  if (!isLinkTopic && !isApplicationTopic) {
    return null;
  }

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen && conversation ? (
      <ConversationSummaryContainer
        conversation={conversation}
        teammateId={teammateId}
        onRequestClose={toggleIsConversationSummaryOpen}
        initiallyExpandedSection={ConversationSummarySectionsEnum.REFERENCE_PREVIEWERS}
        panelId={panelId}
        selectedTopicId={topic.id}
      />
    ) : null;

  const renderPopover = (popoverProps: PopoverTransferProps) => (
    <TopicDropdownContainer
      {...props}
      {...conversationsHandlers}
      {...popoverProps}
      isApplicationTopic={isApplicationTopic}
      onToggleSummary={toggleIsConversationSummaryOpen}
    />
  );

  return (
    <StyledPopoverCoordinatorWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => (
          <ConversationHeaderPill
            hasHover={isOpen}
            renderIcon={() => renderIcon(topicType, application)}
            renderTooltip={() => topic.name}
          >
            <StyledConversationHeaderPillText>{topic.name}</StyledConversationHeaderPillText>
          </ConversationHeaderPill>
        )}
        renderModal={renderPopover}
      />
      {maybeRenderConversationSummary()}
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderIcon(topicType?: TopicTypeModel, application?: ApplicationModel) {
  if (topicType) {
    return (
      <StyledTopicIntegrationWrapper>
        <StyledIntegrationTopicIcon integrationTopicIconUrl={topicType.image} />
      </StyledTopicIntegrationWrapper>
    );
  }
  if (application && application.iconUrl) {
    return (
      <StyledApplicationWrapperDiv>
        <StyledApplicationImg src={application.iconUrl} />
      </StyledApplicationWrapperDiv>
    );
  }
  return <Icon name="topicWebSmall" />;
}
